import {
  MetaSegment,
  OpportunityFavouriteResultCard,
  OrganisationFavouriteResultCard,
  Pagination,
  VolunteerSearchLink,
} from "@skv/core";
import { State } from "@skv/core/src/features/favourites";
import { favouritesStore } from "@skv/state";
import { FAVOURITES_ENUM } from "@skv/state/src/state/favourites";
import { Location } from "history";
import qs from "qs";
import styled from "styled-components";

interface FavouritesSearchResultsMainProps extends State {
  location: Location;
}

export const FavouritesSearchResultsMain = (
  props: FavouritesSearchResultsMainProps
) => {
  const { loading, error, data, location } = props;
  const { favourites, handleFavourite, handleUnfavourite } =
    favouritesStore.hooks.useFavourites();

  if (loading) {
    return (
      <StyledSection className="col-md-9 mb-5">
        <p>Please wait. Loading...</p>
      </StyledSection>
    );
  }

  if (error || !data) {
    return (
      <StyledSection className="col-md-9 mb-5">
        <p>Sorry, something went wrong loading your favourites.</p>
      </StyledSection>
    );
  }

  if (!data.totalCount) {
    return (
      <StyledSection className="col-md-9 mb-5">
        <p>
          Create a list of your favourites. Favourite opportunities and
          organisations using the hearts so you can find them easily.
        </p>
      </StyledSection>
    );
  }

  return (
    <StyledSection className="col-md-9 mb-5">
      <ul className="list-unstyled p-0">
        {data.favourites?.map((fav) => (
          <li key={`${fav.type}-${fav.id}`}>
            {fav.type === "opp" ? (
              <OpportunityFavouriteResultCard
                name={fav.name}
                organisationName={fav.organisationName}
                summary={fav.description}
                category={
                  fav.category?.url ? (
                    <VolunteerSearchLink
                      className="text-dark"
                      to={fav.category.url}
                      searchQuery={{
                        category: [fav.category.value],
                      }}
                      searchType="opp"
                    >
                      {fav.category.name}
                    </VolunteerSearchLink>
                  ) : null
                }
                typesOfWork={<TypesOfWork {...fav.typesOfWork} />}
                to={{
                  pathname: fav.url,
                  state: {
                    referer: `${location.pathname}${location.search}`,
                  },
                }}
                locations={(() => {
                  if (fav.locations.length) {
                    const locations = fav.locations.join(", ");
                    return fav.onlineRemoteVolunteers
                      ? `${locations} / Remote Friendly`
                      : locations;
                  }

                  return <span>Online or Remote</span>;
                })()}
                logoUrl={fav.organisationLogoUrl}
                isFavourited={!!favourites.opportunities[fav.id]}
                onFavourite={() => {
                  handleFavourite(fav.id, FAVOURITES_ENUM.OPPORTUNITY);
                }}
                onUnfavourite={() => {
                  handleUnfavourite(fav.id, FAVOURITES_ENUM.OPPORTUNITY);
                }}
              />
            ) : (
              <OrganisationFavouriteResultCard
                to={{
                  pathname: fav.url,
                  state: {
                    referer: `${location.pathname}${location.search}`,
                  },
                }}
                summary={fav.description}
                locations={(() => {
                  if (fav.locations.length) {
                    return fav.locations[0];
                  }

                  return undefined;
                })()}
                category={
                  fav.category?.url ? (
                    <>
                      <span className="sr-only">Category: </span>
                      <VolunteerSearchLink
                        className="text-dark"
                        to={fav.category.url}
                        searchQuery={{
                          category: [fav.category.value],
                        }}
                        searchType="org"
                      >
                        {fav.category.name}
                      </VolunteerSearchLink>
                    </>
                  ) : null
                }
                organisationName={fav.organisationName}
                isFavourited={!!favourites.organisations[fav.id]}
                onFavourite={() => {
                  handleFavourite(fav.id, FAVOURITES_ENUM.ORGANISATION);
                }}
                onUnfavourite={() => {
                  handleUnfavourite(fav.id, FAVOURITES_ENUM.ORGANISATION);
                }}
                logoUrl={fav.organisationLogoUrl}
              />
            )}
          </li>
        )) || null}
      </ul>
      <Pagination
        currentPage={data.page}
        totalPages={data.totalPages}
        searchQuery={qs.parse(location.search, {
          ignoreQueryPrefix: true,
        })}
        searchType="favourites"
      />
    </StyledSection>
  );
};

const StyledSection = styled.section`
  @media (min-width: ${(p) => p.theme.mdBreakpoint}) {
    padding-left: 4rem;
  }
`;

const TypesOfWork = (props: MetaSegment<number>[]) => {
  if (!props?.length) return null;

  return (
    <>
      {props.map((type, idx) => {
        if (idx === 0) {
          return type.url ? (
            <VolunteerSearchLink
              className="text-dark"
              key={idx}
              to={type.url}
              searchQuery={{
                typeofwork: [type.value],
              }}
              searchType="opp"
            >
              {type.name}
            </VolunteerSearchLink>
          ) : (
            <span>{type.name}</span>
          );
        }

        return `, ${type.name}`;
      })}
    </>
  );
};
